import { HTML_TAG_FILTER_REGEX } from '../central/constants/html-sanitization.constants';

export const hideSecretFromConnectionString = (dbConnectionString: string) =>
  dbConnectionString.replace(/\/\/.*@/, '//***:***@');

export const extractHTMLErrorMessage = (html: string): string => {
  const match = html?.match(/<pre>([\s\S]*?)<\/pre>/);
  if (match) {
    const errorMessage = match[1].replace(/<br>/g, '\n').trim();
    return decodeHtmlEntities(errorMessage);
  }
  return 'Unknown error';
};

const decodeHtmlEntities = (text: string): string => {
  const entities: { [key: string]: string } = {
    '&nbsp;': ' ',
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&apos;': "'",
  };
  return text.replace(/&[a-z]+;/g, (entity) => entities[entity] || entity);
};

export const getFileName = (
  prefix: string,
  startDate: string,
  endDate: string,
  companyName: string,
) => {
  // * Replace every non-alphanumeric character with nothing thus filtering them out
  const parsedCompanyName = companyName
    .toLowerCase()
    .replace(/[^a-zA-Z\d\s:]/gm, '')
    .replace(/\s/g, '-');

  return `${prefix}_${startDate}_${endDate}_${parsedCompanyName}`;
};

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

/**
 * Cleans HTML from text while preserving <a> tags and converting HTML entities back to special characters.
 * - Removes all HTML tags except <a> and </a>
 * - Decodes HTML entities like &nbsp;, &lt;, etc.
 * - Preserves newlines, tabs, and other special characters
 */
export const cleanHTMLPreserveLinks = (text?: string): string => {
  if (!text) {
    return '';
  }

  // Step 1: Remove all HTML tags except allowed ones
  const withoutHtml = text.replace(HTML_TAG_FILTER_REGEX, '').trimStart();

  // Step 2: Decode common HTML entities
  const htmlEntities: Record<string, string> = {
    '&nbsp;': ' ',
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&apos;': "'",
    '&#39;': "'",
    '&ndash;': '–',
    '&mdash;': '—',
  };

  let decoded = withoutHtml;
  for (const [entity, replacement] of Object.entries(htmlEntities)) {
    decoded = decoded.replace(new RegExp(entity, 'g'), replacement);
  }

  return decoded;
};

/**
 * Converts HTML content to Excel-compatible text while preserving anchor tags.
 * Used for data source files to maintain HTML link format for AI training.
 * - Preserves <a href="URL">text</a> tags so AI learns to output proper HTML links
 * - Removes all other HTML tags (strong, em, p, ul, li, h1-h6, etc.)
 * - Decodes HTML entities
 */
export const htmlToExcelText = (text?: string): string => {
  if (!text) {
    return '';
  }

  // Remove all HTML tags except <a> and </a> tags
  // This regex uses negative lookahead to preserve anchor tags
  let parsedText = text.replace(/<(?!\/?a\b)[^>]*>/gi, '').trimStart();

  // Decode common HTML entities
  const htmlEntities: Record<string, string> = {
    '&nbsp;': ' ',
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&apos;': "'",
    '&#39;': "'",
    '&ndash;': '–',
    '&mdash;': '—',
  };

  for (const [entity, replacement] of Object.entries(htmlEntities)) {
    parsedText = parsedText.replace(new RegExp(entity, 'g'), replacement);
  }

  return parsedText;
};

// * Json response can be either an array or an object, handle both cases gracefully
type JSONResponse<T> = T extends unknown[]
  ? T
  : T extends object
    ? Partial<T>
    : T;

export const cleanJSONResponseFromBedrock = <T>(
  text: string,
): JSONResponse<T> | null => {
  try {
    let cleaned = text.trim();

    if (cleaned.startsWith('```json')) {
      cleaned = cleaned.replace(/^```json\s*/, '').replace(/```\s*$/, '');
    } else if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```\s*/, '').replace(/```\s*$/, '');
    }

    return JSON.parse(cleaned) as JSONResponse<T>;
  } catch (error) {
    console.error('Error parsing JSON response from Bedrock:', error);
    return null;
  }
};

/**
 * Escapes every regex metacharacter so a string can be embedded in a RegExp or a
 * Mongo `$regex` and match only itself.
 *
 * Needed whenever user input reaches a regex: unescaped, a `.` or `*` silently
 * changes what matches, and a crafted pattern such as `(a+)+$` can pin the database
 * CPU on catastrophic backtracking.
 */
export const escapeRegex = (value: string): string => {
  if (!value) {
    return '';
  }

  return value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
};
